<template>
	<div
		v-if="rewardGridItems && rewardGridItems.length > 0"
		class="rewards-grid"
		:class="`rewards-grid--${rewardGridItems.length > 1 ? 'multiple' : 'single'} rewards-grid--count-${count} rewards-grid--${context}`"
	>
		<div
			v-for="(item, idx) in rewardGridItems"
			:key="idx"
			class="rewards-grid__item"
		>
			<Tooltip
				v-if="item"
				:heading="translate(`${item.frame_name}.name`)"
				:disabled="hasTileAggregationDetails"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<div
							class="rewards-grid__value-item rewards-grid__value-item--vertical"
							:style="{
								gap: `${(dynamicSize?.gap ?? 16) / 16}rem`,
							}"
						>
							<div
								class="rewards-grid__icon"
								:class="`icon-${getTileIconName(item.frame_name)}-${dynamicSize.icon}`"
							/>

							<div
								class="rewards-grid__label"
								:style="{
									fontSize: `${(dynamicSize?.text ?? 20) / 16}rem`,
								}"
							>
								{{ `${item.qty}x` }}
							</div>
						</div>
					</div>
				</template>
				<template #tooltip>
					<div class="reward-tooltip">
						<p
							v-html="
								item.reward_entity?.description.replace(
									/\{\/?b\}/g,
									'',
								)
							"
						/>
						<TileAggregationDetails
							v-if="item?.reward_entity"
							:entity="item.reward_entity"
							:isPromotion="true"
						/>
					</div>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { FrameName } from "@/game/entities/config";
import type TileEntity from "@/game/entities/TileEntity";
import useTileRepository from "@/hooks/useTileRepository";
import type { BuildingSlotItem } from "@/types/shop";
import { OfferTemplate } from "@/types/shop";
import { Placements, RewardsGridContext } from "@/types/ui";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

interface Props {
	items: BuildingSlotItem[] | null;
	context: RewardsGridContext;
	template: OfferTemplate;
}

const { items, context, template } = defineProps<Props>();

const tilerepo = markRaw(useTileRepository());

type RewardGridItem = BuildingSlotItem & {
	reward_entity?: TileEntity | null;
};

const rewardGridItems = computed((): RewardGridItem[] | null => {
	if (!items) return null;

	return items.map(
		({ group, frame_name, qty }: BuildingSlotItem): RewardGridItem => ({
			group,
			frame_name,
			qty,
			reward_entity: getBuildingEntity(frame_name as FrameName) ?? null,
		}),
	);
});

const count = computed((): number => rewardGridItems.value?.length ?? 0);

const hasTileAggregationDetails = computed((): boolean => {
	if (!rewardGridItems.value || rewardGridItems.value.length < 1)
		return false;
	if (
		context === RewardsGridContext.POPUP &&
		template === OfferTemplate.SLOTS_2 &&
		rewardGridItems.value.length === 1
	)
		return true;
	return false;
});

const dynamicSize = computed((): Record<string, number> => {
	// NOTE GD: Max 5 buildings
	if (context === RewardsGridContext.CARD) {
		return { icon: 60, text: 26, gap: 6 };
	}

	return { icon: 32, text: 30, gap: 6 };
});

const getBuildingEntity = (buildingFrameName: FrameName): TileEntity | null => {
	if (!buildingFrameName) return null;

	const entity = tilerepo.createEntityByFrameName(buildingFrameName);
	if (!entity) return null;

	return entity;
};
</script>

<style lang="scss" scoped>
.rewards-grid {
	@include flex-row-center;
	position: relative;
	gap: 0.625rem;
	z-index: 1;
	padding: 0.625rem;

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		justify-items: center;
		width: 100%;

		.v-popper {
			width: 100%;
		}
	}

	&__value {
		position: relative;
		width: 100%;

		&-item {
			&--horizontal {
				@include flex-row-center;

				.rewards-grid__label {
					text-align: left;
				}
			}

			&--vertical {
				@include flex-col-center;

				.rewards-grid__label {
					text-align: center;
				}
			}
		}
	}

	&__label {
		color: #f4d278;
		font-weight: 800;
		line-height: 1;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}
}
</style>
